/**
 * Attribution publicitaire « first-touch », transmise à l'ERP Spiroux avec
 * chaque demande de devis (bloc `tracking` du webhook de dépôt des leads).
 *
 * Pourquoi une capture à CHAQUE chargement de page, et pas au formulaire :
 * Google Ads pose `gclid` (ou `wbraid` / `gbraid` sur le trafic iOS) sur l'URL
 * de la page d'ARRIVÉE. Le visiteur navigue, puis remplit le formulaire depuis
 * une autre page, où le paramètre a disparu depuis longtemps. Lire l'URL au
 * moment de l'envoi ne remonte presque rien — et personne ne s'en aperçoit,
 * puisque le formulaire fonctionne par ailleurs.
 *
 * First-touch : la première valeur vue gagne, rien n'est écrasé.
 *
 * Consentement (voir lib/consentement.ts) :
 *   · accepté     → conservé 90 jours (localStorage) ;
 *   · pas encore  → conservé le temps de la session seulement, puis promu en
 *                   90 jours si le visiteur accepte ensuite ;
 *   · refusé      → rien n'est capté, et ce qui l'avait été est effacé.
 */
import { lireConsentement } from './consentement';

const CLE = 'gramme_attribution';
const DUREE_MS = 90 * 24 * 60 * 60 * 1000;

const PARAMETRES = [
  'gclid', 'wbraid', 'gbraid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
] as const;

export type Attribution = Partial<Record<(typeof PARAMETRES)[number] | 'referrer' | 'landing_page', string>>;

interface Enregistrement extends Attribution {
  captured_at?: string;
  expire?: number;
}

function lire(stockage: Storage): Enregistrement | null {
  try {
    const brut = stockage.getItem(CLE);
    if (!brut) return null;
    const valeur = JSON.parse(brut) as Enregistrement;
    if (valeur.expire && valeur.expire < Date.now()) {
      stockage.removeItem(CLE);
      return null;
    }
    return valeur;
  } catch {
    return null;
  }
}

function ecrire(stockage: Storage, valeur: Enregistrement): void {
  try {
    stockage.setItem(CLE, JSON.stringify(valeur));
  } catch { /* stockage indisponible (navigation privée stricte) : sans conséquence */ }
}

function effacer(): void {
  try { localStorage.removeItem(CLE); } catch { /* rien */ }
  try { sessionStorage.removeItem(CLE); } catch { /* rien */ }
}

function promouvoir(): void {
  const session = lire(sessionStorage);
  if (session && !lire(localStorage)) ecrire(localStorage, { ...session, expire: Date.now() + DUREE_MS });
}

function capturer(): void {
  const consentement = lireConsentement();
  if (consentement === 'refused') return;

  const url = new URL(window.location.href);
  const trouve: Enregistrement = {};
  for (const p of PARAMETRES) {
    const v = url.searchParams.get(p);
    if (v) trouve[p] = v.slice(0, 500);
  }
  // Référent externe seulement : une navigation interne n'est pas une source.
  const referent = document.referrer;
  if (referent && !referent.startsWith(window.location.origin)) trouve.referrer = referent.slice(0, 500);

  if (Object.keys(trouve).length === 0) return;

  trouve.landing_page = `${url.origin}${url.pathname}`;
  trouve.captured_at = new Date().toISOString();

  if (!lire(sessionStorage)) ecrire(sessionStorage, trouve);
  if (consentement === 'accepted' && !lire(localStorage)) {
    ecrire(localStorage, { ...trouve, expire: Date.now() + DUREE_MS });
  }
}

/** À appeler une fois par chargement de page, depuis le point d'entrée. */
export function installerAttribution(): void {
  if (typeof window === 'undefined') return;
  capturer();
  window.addEventListener('gramme:consentement', (ev) => {
    const decision = (ev as CustomEvent<'accepted' | 'refused' | null>).detail;
    if (decision === 'accepted') promouvoir();
    else effacer();
  });
}

/** Valeurs à joindre à un lead, ou `null` si rien n'est disponible ou autorisé. */
export function lireAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  const consentement = lireConsentement();
  if (consentement === 'refused') return null;
  const valeur = (consentement === 'accepted' ? lire(localStorage) : null) ?? lire(sessionStorage);
  if (!valeur) return null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { captured_at, expire, ...attribution } = valeur;
  return attribution;
}
